import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Briefcase, CheckCircle2, Clock, ChevronRight, Edit, Trash2, Download, Plus, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { listClients, deleteClient, listQuotes, exportUrl, getConfig } from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";
import api from "@/lib/api";

export default function Clients() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("clients");
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [clientTypeFilter, setClientTypeFilter] = useState("");
  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [editName, setEditName] = useState("");
  const [newSupplierName, setNewSupplierName] = useState("");
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [c, q, cfg, exp] = await Promise.all([
        listClients(search),
        api.get("/quotes?include_deleted=true").then(r => r.data),
        getConfig(),
        api.get("/expenses").then(r => r.data).catch(() => []),
      ]);
      setClients(c);
      setQuotes(q);
      setSuppliers(cfg.suppliers || []);
      setExpenses(exp);
    }  finally { setLoading(false); }
  }, [search]);

  const handleAddSupplier = async () => {
    const name = newSupplierName.trim();
    if (!name) { toast.error("Supplier name is required"); return; }
    if (suppliers.some(s => s.toLowerCase() === name.toLowerCase())) { toast.error("Already exists"); return; }
    setAddingSupplier(true);
    // Optimistic insert so the name appears instantly; replaced by server data when the POST returns
    setSuppliers(prev => [...prev, name]);
    try {
      const result = await api.post("/settings/suppliers", { name });
      // Always re-sync from server response (or a fresh fetch as fallback) so DB and UI agree
      const fresh = (result.data?.suppliers && result.data.suppliers.length)
        ? result.data.suppliers
        : (await getConfig()).suppliers || [];
      setSuppliers(fresh);
      setNewSupplierName("");
      setShowAddSupplier(false);
      toast.success(`"${name}" added`);
    } catch (e) {
      // Roll back the optimistic insert
      setSuppliers(prev => prev.filter(s => s !== name));
      toast.error("Failed");
    } finally {
      setAddingSupplier(false);
    }
  };

  const handleDeleteSupplier = async (name) => {
    if (!window.confirm(`Delete supplier "${name}"?`)) return;
    try {
      // Remove from custom suppliers list
      const customDoc = await api.get("/settings/suppliers");
      const currentCustom = customDoc.data?.list || [];
      const updated = currentCustom.filter(s => s !== name);
      await api.put("/settings/suppliers", { list: updated });
      setSuppliers(prev => prev.filter(s => s !== name));
      toast.success("Deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleRenameSupplier = async (oldName) => {
    const newName = editName.trim();
    if (!newName || newName === oldName) { setEditingSupplier(null); return; }
    try {
      const customDoc = await api.get("/settings/suppliers");
      const currentCustom = customDoc.data?.list || [];
      const updated = currentCustom.map(s => s === oldName ? newName : s);
      await api.put("/settings/suppliers", { list: updated });
      setSuppliers(prev => prev.map(s => s === oldName ? newName : s));
      setEditingSupplier(null);
      toast.success("Renamed");
    } catch (e) { toast.error("Failed to rename"); }
  };

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const handleDelete = async () => {
    try {
      await deleteClient(deleteId);
      toast.success("Client deleted");
      setDeleteId(null);
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  // Match jobs to clients by name + email
  const getClientJobs = (client) => {
    const fn = (client.first_name || "").toLowerCase().trim();
    const ln = (client.last_name || "").toLowerCase().trim();
    return quotes.filter(q => {
      const jfn = (q.client_first_name || "").toLowerCase().trim();
      const jln = (q.client_last_name || "").toLowerCase().trim();
      return jfn === fn && jln === ln;
    });
  };

  const totalOpen = clients.reduce((s, c) => {
    return s + getClientJobs(c).filter(j => !["Invoiced", "Cancelled"].includes(j.status) && !j.is_deleted).length;
  }, 0);

  // Memoised unique client types for the filter dropdown
  const clientTypeOptions = useMemo(
    () => [...new Set(clients.map(c => c.client_type).filter(Boolean))].sort(),
    [clients]
  );

  // Filter clients by search (also searches jobs)
  const filteredClients = clients.filter(c => {
    if (clientTypeFilter && c.client_type !== clientTypeFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    if (`${c.first_name} ${c.last_name}`.toLowerCase().includes(s)) return true;
    if (c.email?.toLowerCase().includes(s)) return true;
    const jobs = getClientJobs(c);
    return jobs.some(j =>
      j.site_address?.toLowerCase().includes(s) ||
      j.quote_number?.toLowerCase().includes(s) ||
      j.works_type?.toLowerCase().includes(s) ||
      j.status?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className="px-8 pt-8 pb-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="ui-page-title">Clients & Suppliers</h1>
        </div>
        {/* Tabs */}
        <div className="flex gap-1 mb-6">
          <button onClick={() => setTab("clients")} className="px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: tab === "clients" ? "var(--primary)" : "var(--surface)", color: tab === "clients" ? "#fff" : "var(--text-secondary)", border: `1px solid ${tab === "clients" ? "var(--primary)" : "var(--border)"}` }}>
            Clients ({clients.length})
          </button>
          <button onClick={() => setTab("suppliers")} className="px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: tab === "suppliers" ? "var(--primary)" : "var(--surface)", color: tab === "suppliers" ? "#fff" : "var(--text-secondary)", border: `1px solid ${tab === "suppliers" ? "var(--primary)" : "var(--border)"}` }}>
            <Package className="w-3.5 h-3.5 inline mr-1" /> Suppliers ({suppliers.length})
          </button>
        </div>
      </div>

      {tab === "suppliers" ? (
        <div className="px-8 pb-8">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{suppliers.length} suppliers</p>
            <Button onClick={() => { setNewSupplierName(""); setShowAddSupplier(true); }} className="btn-primary gap-1.5 h-9 px-4 text-sm"><Plus className="w-4 h-4" /> Add Supplier</Button>
          </div>
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <Input value={supplierSearch} onChange={(e) => setSupplierSearch(e.target.value)} placeholder="Search suppliers..." className="pl-10 h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} />
          </div>
          <div className="ui-surface overflow-hidden">
            <table className="ui-table">
              <thead><tr><th>Supplier Name</th><th>Total Spend</th><th>Last Used</th><th></th></tr></thead>
              <tbody>
                {suppliers.length === 0 ? <tr><td colSpan={4}><div className="flex flex-col items-center py-16"><Package className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} /><p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>No suppliers added</p><p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px" }}>Add your first supplier to track spend and usage</p><button onClick={() => { setNewSupplierName(""); setShowAddSupplier(true); }} className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: "#E05C2A", color: "#fff" }}><Plus className="w-4 h-4" /> Add Supplier</button></div></td></tr> :
                [...suppliers]
                  .filter(s => !supplierSearch || s.toLowerCase().includes(supplierSearch.toLowerCase()))
                  .sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }))
                  .map(s => {
                  const supplierExpenses = expenses.filter(e => e.confirmed && (e.merchant || "").toLowerCase() === s.toLowerCase());
                  const totalSpend = supplierExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                  const lastUsed = supplierExpenses.length > 0 ? supplierExpenses.sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0]?.date : null;
                  return (
                  <tr key={s}>
                    <td>
                      {editingSupplier === s ? (
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleRenameSupplier(s); if (e.key === "Escape") setEditingSupplier(null); }} autoFocus className="h-8 w-64 rounded text-sm" style={{ border: "1px solid var(--border)" }} />
                      ) : (
                        <span className="text-sm font-medium">{s}</span>
                      )}
                    </td>
                    <td className="font-mono text-sm">{totalSpend > 0 ? formatCurrency(totalSpend) : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                    <td className="text-xs" style={{ color: "var(--text-muted)" }}>{lastUsed || "—"}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        {editingSupplier === s ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setEditingSupplier(null)} className="h-7 text-xs">Cancel</Button>
                            <Button size="sm" onClick={() => handleRenameSupplier(s)} className="h-7 text-xs btn-primary">Save</Button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => { setEditingSupplier(s); setEditName(s); }} className="p-1.5 rounded hover:bg-slate-100" style={{ color: "var(--text-muted)" }}><Edit className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleDeleteSupplier(s)} className="p-1.5 rounded hover:bg-red-50" style={{ color: "var(--text-muted)" }}><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Add Supplier Dialog */}
          <Dialog open={showAddSupplier} onOpenChange={(o) => { setShowAddSupplier(o); if (!o) setNewSupplierName(""); }}>
            <DialogContent data-testid="add-supplier-dialog">
              <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
              <div className="py-2">
                <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Supplier Name *</Label>
                <Input
                  autoFocus
                  value={newSupplierName}
                  onChange={(e) => setNewSupplierName(e.target.value)}
                  placeholder="e.g. Wickes, Travis Perkins"
                  className="mt-1 rounded-lg"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newSupplierName.trim() && !addingSupplier) {
                      e.preventDefault();
                      handleAddSupplier();
                    }
                  }}
                  data-testid="new-supplier-name-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddSupplier(false)}>Cancel</Button>
                <Button
                  onClick={handleAddSupplier}
                  className="btn-primary gap-1"
                  disabled={!newSupplierName.trim() || addingSupplier}
                  data-testid="add-supplier-submit"
                >
                  <Plus className="w-4 h-4" /> {addingSupplier ? "Adding…" : "Add Supplier"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
      <div className="px-8 pb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{clients.length} clients &middot; {totalOpen} open jobs</p>
          <a href={exportUrl("clients")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: "var(--primary-light)", color: "var(--primary)" }}><Download className="w-3.5 h-3.5" /> Export CSV</a>
        </div>
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients, addresses, quote numbers, job types..." className="pl-10 h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} data-testid="client-search" />
        </div>
        <div className="flex items-center gap-3 mb-4">
          <Select value={clientTypeFilter || "all"} onValueChange={v => setClientTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 h-9 rounded-md text-sm" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}><SelectValue placeholder="All Client Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Client Types</SelectItem>
              {clientTypeOptions.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
            </SelectContent>
          </Select>
          {clientTypeFilter && <button onClick={() => setClientTypeFilter("")} className="text-xs font-semibold" style={{ color: "var(--primary)" }}>Clear</button>}
        </div>
        {loading ? (
          <p className="text-center py-10" style={{ color: "var(--text-muted)" }}>Loading...</p>
        ) : filteredClients.length === 0 ? (
          <p className="text-center py-10" style={{ color: "var(--text-muted)" }}>{search ? "No results found" : "No clients"}</p>
        ) : (
          <div className="ui-surface overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-secondary)", borderColor: "var(--border)" }}>
              <div className="col-span-3">Client Name</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-3">Address</div>
              <div className="col-span-1 text-center">Open Jobs</div>
              <div className="col-span-1 text-center">Completed</div>
              <div className="col-span-2 text-right">Last Activity</div>
              <div className="col-span-1"></div>
            </div>

            {/* Rows */}
            {filteredClients.map(c => {
              const jobs = getClientJobs(c);
              const openJobs = jobs.filter(j => !["Invoiced", "Cancelled"].includes(j.status));
              const completedJobs = jobs.filter(j => ["Invoiced", "Job Signed"].includes(j.status));
              const lastActivity = jobs.length > 0
                ? new Date(Math.max(...jobs.map(j => new Date(j.updated_at || 0)))).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
                : null;
              const expanded = expandedId === c.id;
              const mainAddress = (c.properties || [])[0] || "";

              return (
                <div key={c.id}>
                  <div
                    className="grid grid-cols-12 gap-4 px-5 py-4 border-b items-center cursor-pointer transition-colors hover:bg-slate-50"
                    style={{ borderColor: "var(--border)" }}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    data-testid={`client-row-${c.id}`}
                  >
                    {/* Name + Email */}
                    <div className="col-span-3">
                      <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{c.first_name} {c.last_name}</p>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {(!c.email || c.email.toLowerCase() === "unkown" || c.email.toLowerCase() === "unknown" || c.email.toLowerCase() === "unknwon") ? <em style={{ color: "#9CA3AF" }}>No email on file</em> : c.email}
                      </p>
                    </div>

                    {/* Type badge */}
                    <div className="col-span-1">
                      <span className={`badge-pill text-[10px] ${c.client_type === "Private" ? "badge-warning" : "badge-blue"}`}>
                        {c.client_type}
                      </span>
                    </div>

                    {/* Address */}
                    <div className="col-span-3 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                      <span className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{jobs.length >= 2 ? "Multiple Jobs" : mainAddress || "—"}</span>
                    </div>

                    {/* Open Jobs */}
                    <div className="col-span-1 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: openJobs.length > 0 ? "#D97706" : "var(--text-muted)" }}>
                        <Briefcase className="w-3.5 h-3.5" /> {openJobs.length}
                      </span>
                    </div>

                    {/* Completed */}
                    <div className="col-span-1 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: completedJobs.length > 0 ? "var(--success)" : "var(--text-muted)" }}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> {completedJobs.length}
                      </span>
                    </div>

                    {/* Last Activity */}
                    <div className="col-span-2 text-right">
                      {lastActivity ? (
                        <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--text-secondary)" }}>
                          <Clock className="w-3 h-3" /> {lastActivity}
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: "var(--text-muted)" }}>No activity</span>
                      )}
                    </div>

                    {/* Arrow */}
                    <div className="col-span-1 flex justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteId(c.id); }}
                        className="p-1 rounded hover:bg-red-50 transition-colors"
                        style={{ color: "var(--text-muted)" }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <ChevronRight className="w-4 h-4 transition-transform" style={{ color: "var(--text-muted)", transform: expanded ? "rotate(90deg)" : "none" }} />
                    </div>
                  </div>

                  {/* Expanded: Jobs grouped by property */}
                  {expanded && (
                    <div className="px-5 py-4" style={{ background: "var(--bg)" }}>
                      {jobs.length === 0 ? (
                        <p className="text-xs py-3 text-center" style={{ color: "var(--text-muted)" }}>No jobs for this client</p>
                      ) : (() => {
                        const byAddr = {};
                        jobs.forEach(j => {
                          const addr = (j.site_address || "No address").trim();
                          if (!byAddr[addr]) byAddr[addr] = [];
                          byAddr[addr].push(j);
                        });
                        return (
                          <div className="space-y-5">
                            {Object.entries(byAddr).map(([addr, addrJobs]) => (
                              <div key={addr}>
                                {/* Address label */}
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: addrJobs.length > 1 ? "#FEF3C7" : "var(--surface)", border: `1px solid ${addrJobs.length > 1 ? "#F59E0B" : "var(--border)"}` }}>
                                    <MapPin className="w-3 h-3" style={{ color: addrJobs.length > 1 ? "#D97706" : "var(--text-muted)" }} />
                                  </div>
                                  <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{addr}</span>
                                  {addrJobs.length > 1 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>{addrJobs.length} jobs at this property</span>}
                                </div>

                                {/* Job list — clean rows */}
                                <div className="ml-8 space-y-0">
                                  {addrJobs.map((j, ji) => (
                                    <div key={j.id} onClick={() => navigate(`/quotes/${j.id}`)} className="flex items-center gap-4 py-3 cursor-pointer group transition-colors hover:bg-white/60" style={{ borderBottom: ji < addrJobs.length - 1 ? "1px solid var(--border)" : "none" }}>
                                      <span className="text-sm font-mono font-bold w-16 flex-shrink-0" style={{ color: "var(--primary)" }}>{j.quote_number || "—"}</span>
                                      <div className="flex-1 min-w-0">
                                        <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{j.actions?.[0]?.title || j.job_details?.slice(0, 40) || j.works_type || "Job"}</span>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {j.works_type === "Works Order" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>WO</span>}
                                        {j.is_deleted && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#F3F4F6", color: "#9CA3AF" }}>Archived</span>}
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${j.status === "Invoiced" ? "bg-emerald-100 text-emerald-700" : j.status === "Job Booked" || j.status === "Job Signed" ? "bg-amber-100 text-amber-700" : j.status === "Quote Sent" || j.status === "Awaiting Quote" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{j.status}</span>
                                        {j.total_inc_vat > 0 && <span className="text-sm font-mono font-semibold" style={{ color: "var(--text-primary)" }}>{new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(j.total_inc_vat)}</span>}
                                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--text-muted)" }} />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Client</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
